import React, { useCallback, useEffect, useState } from 'react';
import {
  AppButton,
  Box,
  CrossIcon,
  Heading,
  Inscription,
  Preloader,
  SelectDropdown,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { OptionKey } from '@/common/types';
import { SuccessModal } from '@/components/elements';
import Modal from '@/components/elements/AutoBlockedModal/Modal';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { VOID_REASON_OPTIONS } from '@/modules/employer/components/ReconciliationReports/ChecksList/constants';

const RequiredLabel = styled(Inscription)`
  &:before {
    content: '*';
    color: ${({ theme }) => theme.colors.danger};
    padding-right: 2px;
  }
`;

interface Props {
  checkId?: string;
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
  onUpdateLockbox: () => Promise<void>;
}

const VoidReturnedLockboxCheckModal: React.FC<Props> = ({
  checkId,
  isVisible,
  setIsVisible,
  onUpdateLockbox,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isVoidedSuccessfully, setIsVoidedSuccessfully] = useState(false);
  const { editableLockbox, setEditableLockbox } = useLockboxDetailsStore();

  const setReason = useCallback(
    (value: OptionKey) => {
      setEditableLockbox({ reason: value as string });
    },
    [setEditableLockbox]
  );

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      await onUpdateLockbox();
      setIsVoidedSuccessfully(true);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [onUpdateLockbox]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
  }, [setIsVisible]);

  useEffect(() => {
    return () => {
      setReason('');
    };
  }, [setReason]);

  if (isVoidedSuccessfully) {
    return (
      <SuccessModal
        testId="success_void_check"
        visible
        header={`Check #${checkId} has been voided`}
        buttonText="Got It!"
        onSetVisible={handleClose}
      />
    );
  }

  return (
    <Modal testId="void-check" visible={isVisible} onSetVisible={handleClose}>
      <Heading level="4" fill textAlign="center">
        Void Check
      </Heading>
      <Inscription margin={{ top: 'spacing8' }} textAlign="center" lineHeight="22px" size="large">
        Remember, voiding is irreversible. Optionally, choose a void reason for record-keeping. Verify new
        check details before confirming.
      </Inscription>

      <Box margin={{ vertical: 'spacing24' }} round="moduleRound" pad="spacing24" background="module">
        <Box
          style={{ boxShadow: '0px 6px 15px 0px rgba(0, 0, 0, 0.03)' }}
          pad="spacing24"
          background="canvas"
          round="container1Round"
          border={{ color: 'border2' }}
          direction="row"
          align="center"
          justify="between"
        >
          <RequiredLabel weight="bold" lineHeight="20px" color="textTitle">
            Void reason
          </RequiredLabel>
          <Box width="320px">
            <SelectDropdown
              name="void_reasons"
              testId="void_reasons"
              id="void_reasons"
              options={VOID_REASON_OPTIONS}
              singleMode
              onChange={setReason}
              value={editableLockbox?.reason}
              placeholder="Select void reason"
              {...(editableLockbox?.reason
                ? { rightIcon: <CrossIcon size="small" color="iconPrimary" onClick={() => setReason('')} /> }
                : {})}
            />
          </Box>
        </Box>
      </Box>

      <Box direction="row" justify="end">
        <AppButton
          testId="void-check_cancel"
          buttonType="secondary"
          color="blue"
          onClick={handleClose}
          width="150px"
        >
          Cancel
        </AppButton>
        <Box margin={{ left: 'spacing12' }}>
          <AppButton
            testId="void-check_void"
            buttonType="primary"
            color="red"
            width="150px"
            disabled={isLoading || !editableLockbox?.reason}
            onClick={handleSubmit}
          >
            {isLoading ? <Preloader /> : 'Void'}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default VoidReturnedLockboxCheckModal;
